import { useEffect, useState } from 'react';
import { useNavigate, Link } from "react-router-dom";
import clientInformation from '../login/ClientInfo';
import { IsValidToken } from "../../services/AuthenticationService";
import { GetDepositLimits, GetExpressLimits } from "../../services/DepositService";
import Loading from '../helpers/Loader';

// Safely extract a human-readable message from any error shape.
function getErrorDescription(error) {
    return (
        error?.response?.data?.Authentication?.ErrorDescription ||
        error?.message ||
        'Unknown error'
    );
}

// Whitelist logic for the supported payment methods.
function isSupportedMethod(item) {
    if (
        item.PaymentType === "Credit Card" ||
        item.PaymentType === "Crypto" ||
        item.PaymentType === "Western Union" ||
        item.PaymentType === "Money Gram" ||
        item.PaymentType === "RIA" ||
        item.PaymentType === "Remitly" ||
        item.PaymentType === "BossRev" ||
        item.PaymentType === "Check" ||
        item.PaymentType === "Generic"
    ) {
        return true;
    }
    if (item.PaymentType === "EWallet") {
        // Original quirk preserved: neteller is NOT lower-cased.
        if (item.ImageName === "neteller") return true;
        const image = (item.ImageName || '').toLowerCase();
        return [
            "paysafecard", "paypal", "braintree", "googlepay", "applepay",
            "premieret", "premierint", "premierplus", "pagomovil", "wipay",
            "quickervoucher", "quickervoucherv2", "quickerzelle", "apexglobal", "marketusa",
            "changelly", "quickbuy", "kribatta", "flashteller", "fastgateway", "triplewallet",
            "upay", "venmo", "zelle", "aeropay", "cashapp", "cfach", "cfcashapp", "card"
        ].includes(image);
    }
    return false;
}

export const RegularMethods = () => {
    const clientInfo = clientInformation();

    const [isLoading, setIsLoading] = useState(true);
    const [arrayDeposits, setArrayDeposits] = useState([]);
    const [disableExpress, setDisableExpress] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null); // drives the error screen
    const navigate = useNavigate();
    const HideNavigation = process.env.REACT_APP_HideNavigation;

    // Returns the filtered deposits array (never sets state directly).
    async function DepositsLimits() {
        const response = await GetDepositLimits(localStorage.getItem('Token'));
        const auth = Array.isArray(response?.Authentication) ? response.Authentication : [];
        let i = 0;
        return auth
            .filter(isSupportedMethod)
            .map(item => {
                const {
                    IconURL, DepositName, DepositID, CreditCardTypeID,
                    PaymentType, ImageName, MinDeposit, MaxDeposit, ProcessorID
                } = item;
                return {
                    Key: i++, IconURL, DepositName, DepositID, CreditCardTypeID,
                    PaymentType, ImageName, MinDeposit, MaxDeposit, ProcessorID
                };
            });
    }

    // Returns true when express purchase must be disabled.
    async function ExpressDepositsLimits() {
        const response = await GetExpressLimits(localStorage.getItem('Token'));
        const auth = Array.isArray(response?.Authentication) ? response.Authentication : [];
        return auth.length === 0;
    }

    useEffect(() => {
        let cancelled = false;

        IsValidToken(localStorage.getItem('Token'))
            .then(async function () {
                try {
                    const deposits = await DepositsLimits();
                    const expressDisabled = await ExpressDepositsLimits();
                    if (cancelled) return;

                    setArrayDeposits(deposits);
                    setDisableExpress(expressDisabled);

                    // Decide the empty state HERE, never during render.
                    if (deposits.length === 0 && expressDisabled) {
                        setErrorMsg('There are no purchase methods available for your account at this time. Please contact support.');
                    }
                } catch (error) {
                    if (cancelled) return;
                    clientInfo.Error = false;
                    setErrorMsg('Error getting purchase limits info: ' + getErrorDescription(error));
                } finally {
                    if (!cancelled) setIsLoading(false);
                }
            })
            .catch(function () {
                // Only a real token failure sends the user to /expired/.
                if (!cancelled) navigate('/expired/');
            });

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Side effect moved out of the render body.
    useEffect(() => {
        if (arrayDeposits.length > 0) {
            localStorage.setItem('ArrayRegularMethods', JSON.stringify(arrayDeposits));
        }
    }, [arrayDeposits]);

    function GetFormURL(Key) {
        if (arrayDeposits[Key].PaymentType.toLowerCase() === "credit card")
            return "/deposits/creditcard/" + arrayDeposits[Key].DepositID;
        else if (arrayDeposits[Key].PaymentType.toLowerCase() === "crypto")
            return "/deposits/crypto/" + arrayDeposits[Key].DepositID;
        else if (arrayDeposits[Key].PaymentType.toLowerCase() === "ewallet")
            switch (arrayDeposits[Key].ImageName.toLowerCase()) {
                case "quickerzelle":
                case "quickervoucher":
                    return "/deposits/zelle/" + arrayDeposits[Key].DepositID;
                case "triplewallet":
                    return "/deposits/dualstep/" + arrayDeposits[Key].DepositID;
                case "aeropay":
                    return "/deposits/aeropay/" + arrayDeposits[Key].DepositID;
                case "cfach":
                case "cfcashapp":
                    return "/deposits/coinflow/" + arrayDeposits[Key].DepositID;
                default:
                    return "/deposits/ewallet/" + arrayDeposits[Key].DepositID;
            }
        else if (arrayDeposits[Key].PaymentType.toLowerCase() === "money gram")
            return "/deposits/p2p/moneygram/";
        else if (arrayDeposits[Key].PaymentType.toLowerCase() === "ria")
            return "/deposits/p2p/ria/";
        else if (arrayDeposits[Key].PaymentType.toLowerCase() === "remitly")
            return "/deposits/p2p/remitly/";
        else if (arrayDeposits[Key].PaymentType.toLowerCase() === "bossrev")
            return "/deposits/p2p/bossrev/";
        else if (arrayDeposits[Key].PaymentType.toLowerCase() === "check")
            return "/deposits/check/";
        else if (arrayDeposits[Key].PaymentType.toLowerCase() === "generic")
            return "/deposits/generic/" + arrayDeposits[Key].ProcessorID;
        else
            return "";
    }

    const handlePayoutSelect = (name) => { navigate(`/payouts/${name}`); };
    const handleExpressSelect = () => { navigate(`/main/`); };

    if (isLoading) {
        return <Loading />;
    }

    // Single error screen: covers "both empty" and "load failed".
    if (errorMsg) {
        return (
            <div className="mt-3 componentload">
                <div className="row">
                    <div className="col-12"><hr /></div>
                </div>
                <div className="title">
                    <div className="container text-center">
                        <h4 className="txt-primary text-title-color">No methods available</h4>
                        <p>{errorMsg}</p>
                        <button className="btn box-deposit" onClick={() => navigate('/main/')}>
                            Back to menu
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="mt-3 componentload">
            <div className="row">
                <div className="col-12">
                    <hr />
                </div>
            </div>
            <div className="title">
                <div className="container">
                    <h4 className="txt-primary text-title-color"></h4>
                </div>
            </div>
            <div className="row">
                {HideNavigation == 'true' ? <div></div> :
                    disableExpress == false ?
                        <div className="col-xxl-6 col-md-6 col-sm-6 col-xs-6">
                            <div onClick={() => handleExpressSelect()} className="box-deposit express-color mb-4 text-center text-wrap">
                                <span className="othermethod-text text-center">EXPRESS PURCHASE</span>
                            </div>
                        </div>
                        :
                        <div className="col-xxl-6 col-md-6 col-sm-6 col-xs-6" title="You need to deposit first to have this option available">
                            <div className="box-deposit express-color-disabled mb-4 text-center text-wrap">
                                <span className="othermethod-text text-center">EXPRESS PURCHASE</span>
                            </div>
                        </div>
                }
                {HideNavigation == 'true' ? <div></div> :
                    <div className="col-xxl-6 col-md-6 col-sm-6 col-xs-6">
                        <div onClick={() => handlePayoutSelect('')} className="box-deposit payoutmethod-color mb-4 text-center text-wrap">
                            <span className="othermethod-text text-center">REDEMPTION OPTIONS</span>
                        </div>
                    </div>
                }
            </div>
            <div className="row">
                {arrayDeposits.map((item) => (
                    <div key={item.Key} className="col-xxl-4 col-md-4 col-sm-6 col-xs-6">
                        <Link to={GetFormURL(item.Key)} className="box-deposit  mb-4">
                            <div className="row">
                                <div className="col-md-3 col-sm-12">
                                    <div className="image-wrapper">
                                        <span className="icon-single">
                                            <img
                                                alt=""
                                                src={item.PaymentType.toLowerCase() === 'generic' ? `data:image/svg+xml;base64,${item.ImageName}` : `/svg/${item.ImageName}.svg`}
                                            />
                                        </span>
                                    </div>
                                </div>
                                <div className="col-md-8 col-sm-12 text-center limit-text">
                                    <span>Min:${item.MinDeposit === 0 ? 1 : item.MinDeposit} - Max:{item.MaxDeposit === 0 ? 'No Limit' : '$' + item.MaxDeposit}</span>
                                </div>
                                <div className="col-md-3 limit-text d-none d-md-block "></div>
                            </div>
                        </Link>
                    </div>
                ))}
            </div>
        </div>
    );
}